// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, Divider, Typography, useTheme } from '@mui/material'
import React from 'react'
import stylesDef from './styles';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { GetAdminProjectRoute, GetAdminViewAllOrderAdjustmentsRoute } from 'Utils/routes';
import { useAppDispatch } from 'Hooks';
import { SetSelectedOrderAdjustmentsAction } from 'Features/admin/adminActions';

interface IOrderAdjustmentCardProps {
  orderAdjustment: IOrderAdjustmentsDetails;
}

const OrderAdjustmentCard: React.FC<IOrderAdjustmentCardProps> = ({ orderAdjustment }) => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme);
  const navigate = useNavigate();
  const { orgId } = useParams();
  const dispatch = useAppDispatch();

  // Methods
  const getEditedOnText = (orders: any): string => {
    let text = '';
    if (orders.length > 1) {
      const lastUpdated = orders.reduce((a: any, b: any) => {
        return new Date(a.editedOn) > new Date(b.editedOn) ? a : b;
      });
      text = `Last Edited: ${moment(lastUpdated.editedOn).format('MMMM DD, YYYY hh:mm A')}`;
    } else {
      text = `Edited on: ${moment(orders[0].editedOn).format('MMMM DD, YYYY hh:mm A')}`;
    }
    return text;
  }

  const handleViewProjectClick = () => {
    navigate(GetAdminProjectRoute(orgId, orderAdjustment.projectId));
  };

  const handleViewAllAdjustmentsClick = () => {
    dispatch(SetSelectedOrderAdjustmentsAction(orderAdjustment));
    navigate(GetAdminViewAllOrderAdjustmentsRoute(orgId));
  };

  return (
    <Box sx={styles.orderAdjustmentsWrapper}>
              <Box sx={styles.orderAdjustmentsItemCard}>
                <Box sx={styles.orderAdjustmentsItemCardHeader}>
                  <Box sx={styles.orderAdjustmentsItemCardTitleSection}>
                    <Typography variant="h6" sx={{...styles.noTransformText, ...styles.projectNameText}}>{orderAdjustment.projectName}</Typography>
                    <Button variant="text" color="primary" onClick={handleViewProjectClick}>View Project</Button>
                  </Box>
                  <Typography variant="h6" sx={styles.noTransformText}>{getEditedOnText(orderAdjustment.orderAdjustments)}</Typography>
                </Box>
                {orderAdjustment.orderAdjustments.length === 1 && (
                  <Box sx={styles.orderAdjustmentsItemCardContent}>
                    <Box sx={styles.orderAdjustmentsItemCardContentItem}>
                      <Box sx={styles.orderAdjustmentsItemCardContentItemBox}>
                        <Typography variant="h6" sx={{...styles.noTransformText, ...styles.labelText}}>Date Processed:</Typography>
                        <Typography variant="body1" sx={styles.valueText}>{moment(orderAdjustment.orderAdjustments[0].dateProcessed).format('MMMM DD, YYYY')}</Typography>
                      </Box>
                      <Box sx={styles.orderAdjustmentsItemCardContentItemBox}>
                        <Typography variant="h6" sx={{...styles.noTransformText, ...styles.labelText}}>Delivery Date:</Typography>
                        <Typography variant="body1" sx={styles.valueText}>{orderAdjustment.orderAdjustments[0].deliveryDate ? moment(orderAdjustment.orderAdjustments[0].deliveryDate).format('MMMM DD, YYYY') : 'N/A'}</Typography>
                      </Box>
                    </Box>
                    <Box sx={styles.orderAdjustmentsItemCardContentItem}>
                      <Box sx={styles.orderAdjustmentsItemCardContentItemBox}>
                        <Typography variant="h6" sx={{...styles.noTransformText, ...styles.labelText}}>User Email:</Typography>
                        <Typography variant="body1" sx={styles.valueText}>{orderAdjustment.orderAdjustments[0].userEmail}</Typography>
                      </Box>
                      <Box sx={styles.orderAdjustmentsItemCardContentItemBox}>
                        <Typography variant="h6" sx={{...styles.noTransformText, ...styles.labelText}}>Edited By:</Typography>
                        <Typography variant="body1" sx={styles.valueText}>{orderAdjustment.orderAdjustments[0].editedBy || 'N/A'}</Typography>
                      </Box>
                    </Box>
                    <Box sx={styles.orderAdjustmentsItemCardContentItem}>
                      <Box sx={styles.orderAdjustmentsItemCardContentItemBox}>
                        <Typography variant="h6" sx={{...styles.noTransformText, ...styles.labelText}}>Order Credits:</Typography>
                        <Typography variant="body1" sx={styles.valueText}>{orderAdjustment.orderAdjustments[0].orderCredits.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                <Divider sx={styles.divider} />
                {orderAdjustment.orderAdjustments.length === 1 ? (
                  <Box sx={styles.reasonSection}>
                  <Typography variant="h6" sx={{...styles.noTransformText, ...styles.labelText}}>Reason:</Typography>
                    <Typography variant="body1" sx={styles.valueText}>{orderAdjustment.orderAdjustments[0].reason}</Typography>
                  </Box>
                ) : (
                  <Box sx={styles.viewAllAdjustmentsSection}>
                    <Typography variant="body1" sx={styles.valueText}>{`${orderAdjustment.orderAdjustments.length} Order Adjustments`}</Typography>
                    <Button variant="text" color="primary" onClick={handleViewAllAdjustmentsClick}>View All Adjustments</Button>
                  </Box>
                )}
              </Box>
            </Box>
  )
}

export default OrderAdjustmentCard
